import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { 
  Heart,
  TrendingUp,
  Users,
  Clipboard,
  Mic,
  Accessibility,
  Smile,
  Sparkles,
  Send,
  CheckCircle
} from 'lucide-react';
import Navigation from '../components/Navigation';

const BecomeVolunteerPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    role: '',
    message: ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    console.log('Volunteer application:', formData);
    // Handle form submission
  };

  const whyVolunteer = [
    {
      icon: Users,
      title: 'Community',
      description: 'Join a passionate team serving thousands of attendees with excellence and joy'
    },
    {
      icon: TrendingUp,
      title: 'Growth',
      description: 'Develop leadership skills while building meaningful connections'
    },
    {
      icon: Heart,
      title: 'Service',
      description: 'Make a lasting impact by serving with purpose and compassion'
    }
  ];

  const volunteerRoles = [
    {
      icon: Clipboard,
      title: 'Registration & Info Desk',
      description: 'Welcome attendees, assist with badge pickup, and answer questions'
    },
    {
      icon: Mic,
      title: 'Stage & Production Support',
      description: 'Help with event setup, tech support, and stage coordination'
    },
    {
      icon: Accessibility,
      title: 'Accessibility Assistance',
      description: 'Support guests with special needs and ensure inclusive experience'
    },
    {
      icon: Smile,
      title: 'Guest Relations',
      description: 'Provide directions, manage crowds, and create welcoming atmosphere'
    },
    {
      icon: Sparkles,
      title: 'Prayer & Care Team',
      description: 'Offer prayer, encouragement, and pastoral support to attendees'
    }
  ];

  const requirements = [
    'Must be 18 years or older',
    'Available for full event days (8-hour shifts)',
    'Attend mandatory pre-event orientation'
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navigation />

      {/* Hero Section */}
      <div className="relative h-[500px] flex items-center justify-center overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ 
            backgroundImage: `url('https://images.unsplash.com/photo-1559027615-cd4628902d4a?w=1920&h=1080&fit=crop')`
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0B1B3B]/95 via-[#0B1B3B]/85 to-[#0B1B3B]/75" />
        
        <div className="relative z-10 text-center text-white px-4 max-w-5xl">
          <h1 className="text-5xl md:text-7xl font-bold mb-6 leading-tight">
            Be a <span className="text-[#CFA64B]">Volunteer</span>
          </h1>
          <p className="text-xl md:text-2xl mb-10 text-gray-200">
            Serve. Connect. Lead. Be part of something bigger.
          </p>
          <Button className="bg-[#CFA64B] hover:bg-[#BD954A] text-[#0B1B3B] font-bold px-10 py-6 text-lg">
            Join the Team
          </Button>
        </div>
      </div>

      {/* Section 1: Why Volunteer */}
      <div className="py-20 bg-white">
        <div className="max-w-[1440px] mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-[#0B1B3B] mb-4">Why Volunteer</h2>
            <p className="text-xl text-gray-600">Join a team that helps thousands experience KNOWN CON with joy and excellence</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {whyVolunteer.map((item, idx) => {
              const Icon = item.icon;
              return (
                <Card 
                  key={idx}
                  className="border-2 border-gray-200 hover:border-[#CFA64B] hover:shadow-xl transition-all duration-300 group"
                >
                  <CardContent className="p-8 text-center">
                    <div className="w-20 h-20 mx-auto mb-6 bg-[#CFA64B]/10 group-hover:bg-[#CFA64B] rounded-full flex items-center justify-center transition-colors">
                      <Icon className="w-10 h-10 text-[#CFA64B] group-hover:text-white transition-colors" />
                    </div>
                    <h3 className="text-2xl font-bold text-[#0B1B3B] mb-4">{item.title}</h3>
                    <p className="text-gray-600 leading-relaxed">{item.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </div>

      {/* Section 2: Volunteer Roles */}
      <div className="py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-[1440px] mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-[#0B1B3B] mb-4">Volunteer Roles</h2>
            <p className="text-xl text-gray-600">Find the role that fits your gifts and passion</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {volunteerRoles.map((role, idx) => {
              const Icon = role.icon;
              return (
                <Card 
                  key={idx}
                  className="border-2 border-gray-200 hover:border-[#CFA64B] transition-all duration-300"
                >
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-[#CFA64B]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Icon className="w-6 h-6 text-[#CFA64B]" />
                      </div>
                      <div>
                        <h3 className="text-lg font-bold text-[#0B1B3B] mb-2">{role.title}</h3>
                        <p className="text-sm text-gray-600 leading-relaxed">{role.description}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </div>

      {/* Section 3: Requirements */}
      <div className="py-20 bg-white">
        <div className="max-w-[900px] mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-[#0B1B3B] mb-4">Requirements</h2>
            <p className="text-xl text-gray-600">What you need to know before you apply</p>
          </div>

          <Card className="border-2 border-[#CFA64B] shadow-xl">
            <CardContent className="p-10">
              <ul className="space-y-6">
                {requirements.map((req, idx) => (
                  <li key={idx} className="flex items-start gap-4">
                    <CheckCircle className="w-8 h-8 text-[#CFA64B] flex-shrink-0 mt-1" />
                    <span className="text-xl text-gray-700">{req}</span>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>

          <div className="mt-12 p-8 bg-gradient-to-r from-[#FFF9E9] to-[#FFF5D6] rounded-2xl">
            <h3 className="text-2xl font-bold text-[#0B1B3B] mb-4 text-center">What You'll Receive</h3>
            <ul className="grid md:grid-cols-2 gap-4 text-gray-700">
              <li className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-[#CFA64B]" />
                Free event admission
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-[#CFA64B]" />
                Official volunteer t-shirt
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-[#CFA64B]" />
                Meals during shifts
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-[#CFA64B]" />
                Behind-the-scenes access
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-[#CFA64B]" />
                Certificate of service
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-[#CFA64B]" />
                Volunteer appreciation event
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Section 4: Sign Up to Serve */}
      <div className="py-20" style={{ backgroundColor: '#FFF9E9' }}>
        <div className="max-w-[800px] mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-[#0B1B3B] mb-4">Sign Up to Serve</h2>
            <p className="text-xl text-gray-600">Take the first step in joining our volunteer team</p>
          </div>

          <Card className="border-none shadow-xl">
            <CardContent className="p-8">
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label className="block text-sm font-bold text-[#0B1B3B] mb-2">Name *</label>
                  <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-[#CFA64B] focus:outline-none"
                    placeholder="Your full name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-bold text-[#0B1B3B] mb-2">Email *</label>
                  <input
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-[#CFA64B] focus:outline-none"
                    placeholder="your@email.com"
                  />
                </div>
                <div>
                  <label className="block text-sm font-bold text-[#0B1B3B] mb-2">Phone *</label>
                  <input
                    type="tel"
                    required
                    value={formData.phone}
                    onChange={(e) => setFormData({...formData, phone: e.target.value})}
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-[#CFA64B] focus:outline-none"
                    placeholder="(555) 123-4567"
                  />
                </div>
                <div>
                  <label className="block text-sm font-bold text-[#0B1B3B] mb-2">Role Interest *</label>
                  <select
                    required
                    value={formData.role}
                    onChange={(e) => setFormData({...formData, role: e.target.value})}
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-[#CFA64B] focus:outline-none"
                  >
                    <option value="">Select a role</option>
                    <option value="registration">Registration & Info Desk</option>
                    <option value="stage">Stage & Production Support</option>
                    <option value="accessibility">Accessibility Assistance</option>
                    <option value="guest">Guest Relations</option>
                    <option value="prayer">Prayer & Care Team</option>
                    <option value="any">Any role - I'm flexible!</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-bold text-[#0B1B3B] mb-2">Message</label>
                  <textarea
                    value={formData.message}
                    onChange={(e) => setFormData({...formData, message: e.target.value})}
                    rows={5}
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-[#CFA64B] focus:outline-none resize-none"
                    placeholder="Tell us about yourself and why you want to volunteer..."
                  />
                </div>
                <Button 
                  type="submit"
                  className="w-full bg-[#CFA64B] hover:bg-[#BD954A] text-[#0B1B3B] font-bold py-6 text-lg"
                >
                  <Send className="w-5 h-5 mr-2" />
                  Submit Application
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Section 5: Appreciation Footer */}
      <div className="relative py-24 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-[#0B1B3B] via-[#1a2f5a] to-[#CFA64B]" />
        
        <div className="relative z-10 max-w-4xl mx-auto px-6 text-center text-white">
          <div className="mb-8">
            <Heart className="w-16 h-16 mx-auto text-[#CFA64B]" />
          </div>
          <p className="text-3xl md:text-4xl italic font-light mb-8">
            "Service is the heartbeat of leadership."
          </p>
          <h2 className="text-4xl md:text-6xl font-bold mb-10">
            Thank you for considering to serve with us
          </h2>
          <Button className="bg-white text-[#0B1B3B] hover:bg-gray-100 font-bold px-10 py-6 text-lg">
            Join Volunteer Team
          </Button>
        </div>
      </div>

      {/* Quick Links */}
      <div className="py-6 bg-gray-50 border-t border-gray-200">
        <div className="max-w-[1440px] mx-auto px-6">
          <div className="flex flex-wrap justify-center gap-6 text-sm">
            <a href="/attendee-guide" className="text-[#0B1B3B] hover:text-[#CFA64B] font-medium transition-colors">
              Attendee Guide
            </a>
            <span className="text-gray-400">|</span>
            <a href="/faqs" className="text-[#0B1B3B] hover:text-[#CFA64B] font-medium transition-colors">
              FAQs
            </a>
            <span className="text-gray-400">|</span>
            <a href="#contact" className="text-[#0B1B3B] hover:text-[#CFA64B] font-medium transition-colors">
              Contact Support
            </a>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-[#0f172a] text-white py-12">
        <div className="max-w-[1440px] mx-auto px-6 text-center">
          <p className="text-gray-400 text-sm">© 2025 KNOWN CON. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default BecomeVolunteerPage;
